import React from 'react';
import { Dialog } from '@mui/material';
import { useRoundingRulesForm } from 'hooks/roundingRules/useRoundingRulesForm';
import { useTranslation } from 'react-i18next';
import { RoundingRule } from 'repository/timekeeper/@types/RoundingRules';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';
import { FormDelete } from 'shared/ui/form/FormDelete';
import CloseIcon from '@mui/icons-material/Close';
import { DialogTitle, DialogContent, DialogActions, Button, IconButton, Box } from '@mui/material';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { Form } from './Form';

type Props = {
    open: boolean;
    onClose: () => void;
    rows: RoundingRule[];
    selectedRow: RoundingRule[];
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info';
};

export const RoundingDialog = ({ open, onClose, rows, selectedRow, reloading, mode }: Props) => {
    const { t } = useTranslation();
    const form = useRoundingRulesForm({ rows, onClose, reloading, mode, selectedRow, open });
    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                if (mode === 'delete' || mode === 'info') {
                    onClose();
                } else {
                    form.detectChanges();
                }
            }}
            PaperComponent={PaperComponent}
            maxWidth="md"
            fullWidth
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {mode === 'edit' ? t('settings.fixed_rules.dialog.titles.edit') : t('settings.fixed_rules.dialog.titles.add')}
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={() => {
                    if (mode === 'delete' || mode === 'info') {
                        onClose();
                    } else {
                        form.detectChanges();
                    }
                }}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>{form.alert && <AlertCustom alerts={form.alert} setAlerts={form.setAlert} closeForm={form.cleanForm} t={t} />}</Box>

            <DialogContent dividers>
                {mode === 'delete' ? (
                    <FormDelete
                        open={open}
                        title={t('settings.fixed_rules.dialog.titles.delete')}
                        description={t('settings.fixed_rules.dialog.alerts.delete.message')}
                        names={selectedRow.map((row) => row.name)}
                        onCancel={onClose}
                        onConfirm={form.handleSubmit}
                        textButton={t('general.buttons.delete')}
                        mode="delete"
                    />
                ) : (
                    <Form form={form} mode={mode} />
                )}
            </DialogContent>

            {mode !== 'delete' && mode !== 'info' && (
                <DialogActions>
                    <Button variant="contained" color="error" onClick={form.cleanForm}>
                        {t('general.buttons.cancel')}
                    </Button>
                    <Button variant="contained" color="primary" onClick={form.handleSubmit} disabled={form.disabled()} loading={form.loading}>
                        {t('general.buttons.save')}
                    </Button>
                </DialogActions>
            )}

            {mode === 'info' && (
                <DialogActions>
                    <Button variant="contained" color="error" onClick={onClose}>
                        {t('general.buttons.close')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
